import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import type {
  AuthUser,
  LoginResponse,
  OtpSetupStartResponse,
  Employer,
} from '@pm-next/shared-types';
import {
  login as loginApi,
  verifyOtp as verifyOtpApi,
  startOtpSetup as startOtpSetupApi,
  verifyOtpSetup as verifyOtpSetupApi,
  logout as logoutApi,
  getCurrentUser,
  exchangeTokenApi,
  selectEmployer as selectEmployerApi,
  getMyEmployers as getMyEmployersApi,
  switchEmployer as switchEmployerApi,
} from '@/api/auth';
import {
  setTokens,
  clearTokens,
  getAccessToken,
  getEmployerIdFromAccessToken,
  subscribeAuthSync,
} from '@/auth/token-storage';
import { clearCompanyScopedCache } from '@/lib/query-client';

export type LoginStep =
  | 'checking'
  | 'credentials'
  | 'otp'
  | 'otp-setup'
  | 'employer-select'
  | 'no-access'
  | null;

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginStep: LoginStep;
  pendingToken: string | null;
  pendingUser: { id: number; email: string } | null;
  otpSetupData: OtpSetupStartResponse | null;
  employers: Employer[];
  pendingEmployerToken: string | null;
  selectedEmployer: Employer | null;
  tryAutoExchange: (tenantToken?: string, employerCode?: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<LoginResponse>;
  verifyOtp: (otp: string) => Promise<void>;
  startOtpSetup: () => Promise<OtpSetupStartResponse>;
  verifyOtpSetup: (otp: string) => Promise<void>;
  selectEmployer: (employerId: number) => Promise<void>;
  fetchMyEmployers: () => Promise<Employer[]>;
  switchEmployer: (employerId: number) => Promise<void>;
  logout: () => Promise<void>;
  resetLoginState: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginStep, setLoginStep] = useState<LoginStep>('checking');
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<{ id: number; email: string } | null>(null);
  const [otpSetupData, setOtpSetupData] = useState<OtpSetupStartResponse | null>(null);
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [pendingEmployerToken, setPendingEmployerToken] = useState<string | null>(null);
  const [selectedEmployer, setSelectedEmployer] = useState<Employer | null>(null);
  const initializedRef = useRef(false);
  const selectedEmployerRef = useRef<Employer | null>(null);
  const userRef = useRef<AuthUser | null>(null);
  const syncingRef = useRef(false);
  const isLoadingRef = useRef(true);

  selectedEmployerRef.current = selectedEmployer;
  userRef.current = user;
  isLoadingRef.current = isLoading;

  const loadUser = useCallback(async () => {
    try {
      const userData = await getCurrentUser();
      setUser(userData);
      if (userData.employer) {
        setSelectedEmployer(userData.employer);
      }
      setLoginStep(null);
      return true;
    } catch {
      return false;
    }
  }, []);

  const applyLoggedOutState = useCallback(() => {
    clearCompanyScopedCache();
    setUser(null);
    setLoginStep('credentials');
    setPendingToken(null);
    setPendingUser(null);
    setOtpSetupData(null);
    setEmployers([]);
    setPendingEmployerToken(null);
    setSelectedEmployer(null);
  }, []);

  /** Align this tab with tokens written by another tab (company switch or logout). */
  const syncSessionFromOtherTab = useCallback(async () => {
    if (syncingRef.current || isLoadingRef.current) return;
    syncingRef.current = true;
    try {
      const token = getAccessToken();
      if (!token) {
        if (userRef.current) {
          applyLoggedOutState();
        }
        return;
      }

      const tokenEmployerId = getEmployerIdFromAccessToken(token);
      const localEmployerId = selectedEmployerRef.current?.id ?? null;

      // Still logged in but company (or session) changed in another tab
      if (tokenEmployerId !== localEmployerId) {
        clearCompanyScopedCache();
        const loaded = await loadUser();
        if (!loaded) {
          clearTokens();
          applyLoggedOutState();
        }
      }
    } finally {
      syncingRef.current = false;
    }
  }, [applyLoggedOutState, loadUser]);

  useEffect(() => {
    const unsubscribe = subscribeAuthSync((message) => {
      if (message.type === 'logout') {
        if (userRef.current) applyLoggedOutState();
        return;
      }
      void syncSessionFromOtherTab();
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void syncSessionFromOtherTab();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [applyLoggedOutState, syncSessionFromOtherTab]);

  const tryAutoExchange = useCallback(
    async (tenantToken?: string, employerCode?: string): Promise<boolean> => {
      setLoginStep('checking');

      const token = tenantToken?.trim();
      const code = employerCode?.trim();

      if (token && code) {
        try {
          const result = await exchangeTokenApi(token, code);
          if (result.ok && result.accessToken && result.refreshToken) {
            setTokens(result.accessToken, result.refreshToken);
            if (result.employer) {
              setSelectedEmployer(result.employer);
            }
            await loadUser();
            return true;
          }
        } catch {
          setLoginStep('no-access');
          return false;
        }
      }

      if (!getAccessToken()) {
        setLoginStep('credentials');
        return false;
      }

      const loaded = await loadUser();
      if (!loaded) {
        setLoginStep('credentials');
      }
      return loaded;
    },
    [loadUser]
  );

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const init = async () => {
      setIsLoading(true);
      const tenantToken = typeof window !== 'undefined' ? window.tenantAccessToken?.trim() : '';
      const employerCode = typeof window !== 'undefined' ? window.employerCode?.trim() : '';
      if (tenantToken) {
        clearTokens();
      }
      if (tenantToken && employerCode) {
        await tryAutoExchange(tenantToken, employerCode);
      } else {
        await tryAutoExchange();
      }
      setIsLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      const response = await loginApi(email, password);

      if (response.requiresOtp && response.pendingToken) {
        setPendingToken(response.pendingToken);
        setPendingUser(response.user || null);
        setLoginStep('otp');
      } else if (
        response.requiresEmployerSelect &&
        response.pendingEmployerToken &&
        response.employers
      ) {
        setPendingEmployerToken(response.pendingEmployerToken);
        setEmployers(response.employers);
        setPendingUser(response.user || null);
        setLoginStep('employer-select');
      } else if (response.ok && response.accessToken && response.refreshToken) {
        setTokens(response.accessToken, response.refreshToken);
        if (response.employer) {
          setSelectedEmployer(response.employer);
        }
        const loaded = await loadUser();
        if (!loaded && response.user) {
          setUser({
            id: String(response.user.id),
            email: response.user.email,
            roles: [],
          });
          setLoginStep(null);
        }
      }

      return response;
    },
    [loadUser]
  );

  const verifyOtp = useCallback(
    async (otp: string): Promise<void> => {
      if (!pendingToken) {
        throw new Error('No pending token');
      }

      const response = await verifyOtpApi(pendingToken, otp);

      if (response.requiresEmployerSelect && response.pendingEmployerToken && response.employers) {
        setPendingToken(null);
        setPendingEmployerToken(response.pendingEmployerToken);
        setEmployers(response.employers);
        setLoginStep('employer-select');
        return;
      }

      if (response.ok && response.accessToken && response.refreshToken) {
        setTokens(response.accessToken, response.refreshToken);
        setPendingToken(null);
        if (response.employer) {
          setSelectedEmployer(response.employer);
        }
        const loaded = await loadUser();
        if (!loaded && pendingUser) {
          setUser({
            id: String(pendingUser.id),
            email: pendingUser.email,
            roles: [],
          });
          setLoginStep(null);
        }
        setPendingUser(null);
      }
    },
    [pendingToken, pendingUser, loadUser]
  );

  const startOtpSetup = useCallback(async (): Promise<OtpSetupStartResponse> => {
    if (!pendingToken) {
      throw new Error('No pending token');
    }

    const response = await startOtpSetupApi(pendingToken);
    setOtpSetupData(response);
    return response;
  }, [pendingToken]);

  const verifyOtpSetup = useCallback(
    async (otp: string): Promise<void> => {
      if (!pendingToken) {
        throw new Error('No pending token');
      }

      const response = await verifyOtpSetupApi(pendingToken, otp);

      if (response.requiresEmployerSelect && response.pendingEmployerToken && response.employers) {
        setPendingToken(null);
        setOtpSetupData(null);
        setPendingEmployerToken(response.pendingEmployerToken);
        setEmployers(response.employers);
        setLoginStep('employer-select');
        return;
      }

      if (response.ok && response.accessToken && response.refreshToken) {
        setTokens(response.accessToken, response.refreshToken);
        setPendingToken(null);
        setPendingUser(null);
        setOtpSetupData(null);
        if (response.employer) {
          setSelectedEmployer(response.employer);
        }
        await loadUser();
      }
    },
    [pendingToken, loadUser]
  );

  const selectEmployer = useCallback(
    async (employerId: number): Promise<void> => {
      if (!pendingEmployerToken) {
        throw new Error('No pending employer token');
      }

      const response = await selectEmployerApi(pendingEmployerToken, employerId);
      if (response.ok && response.accessToken && response.refreshToken) {
        setTokens(response.accessToken, response.refreshToken);
        setPendingEmployerToken(null);
        setEmployers([]);
        setSelectedEmployer(response.employer);
        const loaded = await loadUser();
        if (!loaded && pendingUser) {
          setUser({
            id: String(pendingUser.id),
            email: pendingUser.email,
            roles: [],
          });
          setLoginStep(null);
        }
        setPendingUser(null);
      }
    },
    [pendingEmployerToken, pendingUser, loadUser]
  );

  const fetchMyEmployers = useCallback(async (): Promise<Employer[]> => {
    const response = await getMyEmployersApi();
    setEmployers(response.employers);
    return response.employers;
  }, []);

  const switchEmployer = useCallback(
    async (employerId: number): Promise<void> => {
      const response = await switchEmployerApi(employerId);
      if (response.ok && response.accessToken && response.refreshToken) {
        setTokens(response.accessToken, response.refreshToken);
        clearCompanyScopedCache();
        setSelectedEmployer(response.employer);
        await loadUser();
      }
    },
    [loadUser]
  );

  const logout = useCallback(async (): Promise<void> => {
    clearTokens();
    clearCompanyScopedCache();
    await logoutApi();
    applyLoggedOutState();
  }, [applyLoggedOutState]);

  const resetLoginState = useCallback(() => {
    setLoginStep('credentials');
    setPendingToken(null);
    setPendingUser(null);
    setOtpSetupData(null);
    setEmployers([]);
    setPendingEmployerToken(null);
    setSelectedEmployer(null);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    loginStep,
    pendingToken,
    pendingUser,
    otpSetupData,
    employers,
    pendingEmployerToken,
    selectedEmployer,
    tryAutoExchange,
    login,
    verifyOtp,
    startOtpSetup,
    verifyOtpSetup,
    selectEmployer,
    fetchMyEmployers,
    switchEmployer,
    logout,
    resetLoginState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
